import type React from 'react';
import { memo, useEffect, useState } from 'react';

import { IconChevronDownOutline, IconChevronUpOutline } from '@zillow/constellation-icons';

import { Icon, Menu, TextButton } from '@zillow/constellation-10';

type TriggerTitleProps = {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
};

type Props = {
  title: React.ReactElement<TriggerTitleProps>;
  useLeftNav?: boolean;
  // allows parent to control the dropdown open state via prop for left nav layout
  pIsOpen?: boolean;
  //  ZRM-16458: hide the trailing chevron (IA top bar wants an icon-only trigger)
  hideChevron?: boolean;
  onClose?(): void;
  onOpen?(): void;
  children?: React.ReactNode;
};

const DropdownMenu = ({
  onClose,
  onOpen,
  title,
  useLeftNav = false,
  pIsOpen = false,
  hideChevron = false,
  children = null,
}: Props) => {
  const [isOpen, setIsOpen] = useState(pIsOpen);

  useEffect(() => {
    if (useLeftNav && pIsOpen !== isOpen) {
      setIsOpen(pIsOpen);
    }
  }, [pIsOpen, useLeftNav]);

  const IconChevron = isOpen ? IconChevronUpOutline : IconChevronDownOutline;
  const chevronIcon =
    useLeftNav === false && !hideChevron ? (
      <Icon render={<IconChevron />} size="sm" css={{ color: 'icon.accent.brand.hero', position: 'relative' }} />
    ) : null;
  const renderTextButtonTrigger = (textButtonTitle: React.ReactElement<TriggerTitleProps>) => {
    const { children: titleChildren, icon, iconPosition = 'start', ...titleRootProps } = textButtonTitle.props;

    return (
      <TextButton.Root {...titleRootProps}>
        {icon && iconPosition === 'start' ? <TextButton.Icon>{icon}</TextButton.Icon> : null}
        <TextButton.Label>{titleChildren}</TextButton.Label>
        {icon && iconPosition === 'end' ? <TextButton.Icon>{icon}</TextButton.Icon> : null}
        {chevronIcon}
      </TextButton.Root>
    );
  };
  const trigger = chevronIcon ? renderTextButtonTrigger(title) : title;

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    if (useLeftNav === false) {
      setIsOpen(false);
    }
  };

  const handleOpen = () => {
    if (onOpen) {
      onOpen();
    }

    if (useLeftNav === false) {
      setIsOpen(true);
    }
  };

  return (
    <Menu.Root
      onOpenChange={(open) => (open ? handleOpen() : handleClose())}
      offset={useLeftNav ? { mainAxis: 175, crossAxis: 20 } : undefined}
      placement={useLeftNav ? 'right' : 'bottom-start'}
      data-test="DropdownMenu"
      open={isOpen}
    >
      <Menu.Trigger>{trigger}</Menu.Trigger>
      <Menu.Portal role="Menu">
        <Menu.Content css={{ display: { base: 'none', xxl: 'block' } }}>{children}</Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
};

export default memo(DropdownMenu);
